import { useState, useEffect } from 'react';
import { useGamificationStore, BADGES_CATALOG } from '@/data/gamificationStore';
import { useAuth } from '@/contexts/AuthContext';
import { Trophy, Star, ShieldAlert, Award, X, ChevronUp, ChevronDown } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useLocation } from 'react-router-dom';
import { useSettingsStore } from '@/data/settingsStore';

export default function CitizenBadgesPanel() {
  const { xp, level, badges, syncWithAuth } = useGamificationStore();
  const { user, updateUser, isAuthenticated } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [justLeveledUp, setJustLeveledUp] = useState(false);
  
  const location = useLocation();
  const settings = useSettingsStore(state => state.settings);

  // Synchronisation avec AuthContext si l'utilisateur se connecte
  useEffect(() => {
    if (isAuthenticated && user?.metadata?.gamification) {
      syncWithAuth(user.metadata.gamification);
    }
  }, [isAuthenticated, user?.metadata?.gamification]);

  // Synchronisation inverse : quand on gagne des XP et qu'on est connecté, on update le backend (debounced / ou sur event)
  useEffect(() => {
    if (isAuthenticated && user?.role === 'CITIZEN' && xp > 0) {
      const timer = setTimeout(() => {
        updateUser({
          metadata: {
            ...user?.metadata,
            gamification: { xp, badges }
          }
        });
      }, 5000); // 5 sec debounce
      return () => clearTimeout(timer);
    }
  }, [xp, badges.length, isAuthenticated, user?.role]);

  // Écoute des événements de gamification pour les Toasts
  useEffect(() => {
    const handleXpGained = (e: any) => {
      const { amount, reason } = e.detail;
      toast.success(`+${amount} XP ${reason ? `(${reason})` : ''}`, {
        icon: '🌟',
        duration: 3000,
        position: 'bottom-right'
      });
    };

    const handleLevelUp = (e: any) => {
      setJustLeveledUp(true);
      toast(`🎉 Niveau Supérieur !`, {
        description: `Vous avez atteint le niveau ${e.detail.level} !`,
        duration: 5000,
        position: 'bottom-right'
      });
      setTimeout(() => setJustLeveledUp(false), 5000);
    };

    const handleBadgeUnlocked = (e: any) => {
      const badgeInfo = BADGES_CATALOG.find(b => b.id === e.detail.badgeId);
      if (badgeInfo) {
        toast(`Nouveau Badge Débloqué !`, {
          description: `${badgeInfo.icon} ${badgeInfo.name}`,
          duration: 6000,
          position: 'bottom-right'
        });
      }
    };

    window.addEventListener('kaynin_xp_gained', handleXpGained);
    window.addEventListener('kaynin_level_up', handleLevelUp);
    window.addEventListener('kaynin_badge_unlocked', handleBadgeUnlocked);

    return () => {
      window.removeEventListener('kaynin_xp_gained', handleXpGained);
      window.removeEventListener('kaynin_level_up', handleLevelUp);
      window.removeEventListener('kaynin_badge_unlocked', handleBadgeUnlocked);
    };
  }, []);

  const xpForNextLevel = Math.pow(level, 2) * 100;
  const xpForCurrentLevel = Math.pow(level - 1, 2) * 100;
  const progressPercent = ((xp - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100;

  // Settings for gamification badge
  const isEnabled = settings['kaynin_badge_enabled'] !== 'false';
  const position = settings['kaynin_badge_position'] || 'left';
  
  let allowedPaths = ['/'];
  try {
    const p = settings['kaynin_badge_paths'];
    if (p) allowedPaths = typeof p === 'string' ? JSON.parse(p) : p;
  } catch(e) {}

  // Cacher le panel pour les Admins, Partis, etc. (On le garde pour CITIZEN ou non-connectés)
  if (user && user.role !== 'CITIZEN') {
    return null;
  }

  // Ne pas afficher si désactivé ou sur une page non autorisée
  if (!isEnabled || !allowedPaths.includes(location.pathname)) {
    return null;
  }

  const positionClasses = position === 'right' 
    ? 'fixed bottom-6 right-6' 
    : 'fixed bottom-6 left-6';

  return (
    <div className={`${positionClasses} z-50 animate-in fade-in slide-in-from-bottom-5`}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <button 
            className={`flex items-center gap-2 rounded-full p-3 shadow-lg transition-all hover:scale-105 active:scale-95 ${justLeveledUp ? 'bg-amber-500 animate-bounce text-white' : 'bg-primary text-primary-foreground'}`}
          >
            <div className="relative">
              <Trophy className="w-6 h-6" />
              {badges.length > 0 && (
                <span className="absolute -top-2 -right-2 bg-destructive text-white text-[10px] font-bold w-4 h-4 flex items-center justify-center rounded-full">
                  {badges.length}
                </span>
              )}
            </div>
            <div className="hidden md:flex flex-col items-start px-1">
              <span className="text-xs font-bold leading-none">Niv {level}</span>
              <span className="text-[10px] opacity-80 font-medium">{xp} XP</span>
            </div>
            {isOpen ? <ChevronDown className="w-4 h-4 ml-1 opacity-70" /> : <ChevronUp className="w-4 h-4 ml-1 opacity-70" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-0 rounded-2xl shadow-xl border-border/50 mb-2 mr-2" align="end" sideOffset={10}>
          <div className="bg-gradient-to-br from-primary to-accent p-5 text-white rounded-t-2xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-white/20 flex items-center justify-center border-2 border-white/40 shadow-inner">
                <Trophy className="w-6 h-6 text-yellow-300" />
              </div>
              <div>
                <h3 className="font-display font-bold text-lg leading-tight">Citoyen Kayn'in</h3>
                <p className="text-sm font-medium text-white/80">Niveau {level}</p>
              </div>
            </div>
            
            <div className="space-y-1.5">
              <div className="flex justify-between text-xs font-bold text-white/90">
                <span>{xp} XP</span>
                <span>{xpForNextLevel} XP</span>
              </div>
              <Progress value={progressPercent} className="h-2.5 bg-black/20" indicatorClassName="bg-yellow-400" />
              <p className="text-[10px] text-center text-white/70 font-medium">
                +{xpForNextLevel - xp} XP pour le niveau {level + 1}
              </p>
            </div>
          </div>
          
          <div className="p-4 bg-card rounded-b-2xl max-h-[300px] overflow-y-auto">
            <h4 className="font-bold text-sm text-foreground flex items-center gap-2 mb-3">
              <Award className="w-4 h-4 text-primary" />
              Vos Badges ({badges.length}/{BADGES_CATALOG.length})
            </h4>
            <div className="grid grid-cols-1 gap-2">
              {BADGES_CATALOG.map(badge => {
                const unlocked = badges.includes(badge.id);
                return (
                  <div 
                    key={badge.id} 
                    className={`flex items-center gap-3 p-2 rounded-xl border ${unlocked ? 'bg-primary/5 border-primary/20' : 'bg-muted/30 border-transparent opacity-60 grayscale'}`}
                  >
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-lg shadow-sm ${unlocked ? badge.color : 'bg-muted-foreground'}`}>
                      {badge.icon}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-bold leading-tight">{badge.name}</p>
                      <p className="text-xs text-muted-foreground leading-tight mt-0.5">{badge.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
